import React from 'react'
import ReactMarkdown from 'utils/markdown'
import SectionType from 'models/types/ts/sectionType'
import ContentType from 'models/types/ts/contentType'
import { Container, Heading, Button, Frame } from 'layouts'
import { HeadingXSmall, ParagraphRegular } from 'layouts/typography'
import GuidelinesLightBox from '../guidelines'
import style from './Requirements.module.scss'

interface Props {
    section: SectionType
    guidelines: SectionType
}

const Requirements = ({ section, guidelines }: Props): JSX.Element => {
    const {
        headline,
        teaser,
        body,
        buttonUrl,
        buttonText,
        contentCollection: { total, items },
    } = section

    function renderHeading(children: React.ReactNode): JSX.Element {
        return <HeadingXSmall as="h3">{children}</HeadingXSmall>
    }

    return (
        <Frame cutoutBottom={false} medium className={style.req}>
            <Container>
                <Heading size="regular">{headline}</Heading>
                <div className={style.text}>
                    <ReactMarkdown
                        escapeHtml={false}
                        source={teaser}
                        renderers={{
                            h1: ({ children }) => renderHeading(children),
                            h2: ({ children }) => renderHeading(children),
                            h3: ({ children }) => renderHeading(children),
                            h4: ({ children }) => renderHeading(children),
                            h5: ({ children }) => renderHeading(children),
                            h6: ({ children }) => renderHeading(children),
                            p: ({ children }) => (
                                <ParagraphRegular>{children}</ParagraphRegular>
                            ),
                        }}
                    />
                </div>
                <div className={style.blocks}>
                    {total > 0 &&
                        items.map((item: ContentType, index, array) => (
                            <div className={style.block} key={item.name}>
                                <div
                                    className={style.inner}
                                    key={item.headline}
                                >
                                    <HeadingXSmall as="h3" fontSize={[20, 22]}>
                                        {item.headline}
                                    </HeadingXSmall>
                                    <ReactMarkdown
                                        escapeHtml={false}
                                        source={item.body}
                                    />
                                </div>
                                {index === array.length - 1 && (
                                    <GuidelinesLightBox section={guidelines} />
                                )}
                            </div>
                        ))}
                </div>
                <ReactMarkdown
                    escapeHtml={false}
                    source={body}
                    className={style.desc}
                />
                {buttonUrl && (
                    <Button
                        size="small"
                        internal
                        path={{
                            page: 'partners',
                            subpage: buttonUrl,
                        }}
                        custom={{
                            background: '#CD923B',
                        }}
                    >
                        {buttonText}
                    </Button>
                )}
            </Container>
        </Frame>
    )
}

export default Requirements
